import React, { createContext, useContext, useState } from 'react'
import { detectNoteType } from '../utils/noteTypeDetection'

/**
 * Tabs Context for managing multiple note tabs
 * 
 * Allows users to open multiple notes in tabs and switch between them
 */

const TabsContext = createContext({})

export const useTabs = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('useTabs must be used within a TabsProvider')
  }
  return context
}

export const TabsProvider = ({ children }) => {
  const [tabs, setTabs] = useState([])
  const [activeTabId, setActiveTabId] = useState(null)

  /**
   * Open a new tab or switch to existing tab
   */
  const openTab = (tabData) => {
    const { type, noteId, title = 'Untitled', note = null, isExpanded = false, noteType = null } = tabData
    
    // Ensure title is always a string
    const safeTitle = (title && typeof title === 'string') ? title : 'Untitled'
    
    // Detect note type if not provided
    const detectedNoteType = noteType || detectNoteType(note)
    
    // Check if tab already exists
    const existingTab = tabs.find(tab => 
      tab.noteId === noteId && tab.type === type
    )
    
    if (existingTab) {
      // Switch to existing tab
      setActiveTabId(existingTab.id)
      return existingTab.id
    }
    
    // Create new tab
    const newTab = {
      id: `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type, // 'view', 'edit', 'new'
      noteId,
      title: safeTitle,
      note,
      noteType: detectedNoteType, // Add note type support
      isDirty: false, // Track if note has unsaved changes
      formData: type === 'new' ? { title: '', content: '' } : null, // Store form data for new tabs, edit tabs get formData when user makes changes
      isExpanded: isExpanded, // Track if tab should open in fullscreen mode
      createdAt: new Date()
    }
    
    setTabs(prev => [...prev, newTab])
    setActiveTabId(newTab.id)
    return newTab.id
  }

  /**
   * Close a tab
   */
  const closeTab = (tabId) => {
    setTabs(prev => {
      const updatedTabs = prev.filter(tab => tab.id !== tabId)
      
      // If closing active tab, switch to another tab
      if (activeTabId === tabId) {
        if (updatedTabs.length > 0) {
          // Switch to the next tab or the last one
          const currentIndex = prev.findIndex(tab => tab.id === tabId)
          const nextTab = updatedTabs[currentIndex] || updatedTabs[updatedTabs.length - 1]
          setActiveTabId(nextTab.id)
        } else {
          setActiveTabId(null)
        }
      }
      
      return updatedTabs
    })
  }

  /**
   * Close all tabs
   */
  const closeAllTabs = () => {
    setTabs([])
    setActiveTabId(null)
  }

  /**
   * Close tabs except the specified one
   */
  const closeOtherTabs = (tabId) => {
    const tabToKeep = tabs.find(tab => tab.id === tabId)
    if (tabToKeep) {
      setTabs([tabToKeep])
      setActiveTabId(tabId)
    }
  }

  /**
   * Switch to a specific tab
   */
  const switchToTab = (tabId) => {
    const tab = tabs.find(tab => tab.id === tabId)
    if (tab) {
      setActiveTabId(tabId)
    }
  }

  /**
   * Update tab data (e.g., title, dirty status)
   */
  const updateTab = (tabId, updates) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, ...updates } : tab
    ))
  }

  /**
   * Get active tab
   */
  const getActiveTab = () => {
    return tabs.find(tab => tab.id === activeTabId) || null
  }

  /**
   * Check if a note is already open in a tab
   */
  const isNoteOpen = (noteId, type = null) => {
    return tabs.some(tab => 
      tab.noteId === noteId && (type ? tab.type === type : true)
    )
  }

  /**
   * Get tab by note ID and type
   */
  const getTabByNote = (noteId, type) => {
    return tabs.find(tab => tab.noteId === noteId && tab.type === type)
  }

  /**
   * Cycle to next tab
   */
  const cycleToNextTab = () => {
    if (tabs.length <= 1) return

    const currentIndex = tabs.findIndex(tab => tab.id === activeTabId)
    const nextIndex = currentIndex === -1 || currentIndex >= tabs.length - 1 ? 0 : currentIndex + 1
    setActiveTabId(tabs[nextIndex].id)
  }

  /**
   * Cycle to previous tab
   */
  const cycleToPreviousTab = () => {
    if (tabs.length <= 1) return

    const currentIndex = tabs.findIndex(tab => tab.id === activeTabId)
    const prevIndex = currentIndex === -1 || currentIndex <= 0 ? tabs.length - 1 : currentIndex - 1
    setActiveTabId(tabs[prevIndex].id)
  }

  const value = {
    // State
    tabs,
    activeTabId,
    
    // Methods
    openTab,
    closeTab,
    closeAllTabs,
    closeOtherTabs,
    switchToTab,
    updateTab,
    getActiveTab,
    isNoteOpen,
    getTabByNote,
    cycleToNextTab,
    cycleToPreviousTab,
  }

  return (
    <TabsContext.Provider value={value}>
      {children}
    </TabsContext.Provider>
  )
}

export default TabsContext 